import { Box, Typography } from "@mui/material";
import React from "react";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { levelXPTable } from "../@shared/utils/levelXPTable";
import { MainContext } from "../App";
import {
  getChromePalette,
  getContentTextShadow,
  getIconMedallionPalette,
  getWrapperFilter,
} from "./neoSvgUtils";

function FrameSvg({ idPrefix, palette, shape = "compact", disabled }) {
  const fillBase = disabled ? palette.disabledBase : palette.base;
  const fillTop = disabled ? palette.disabledBase : palette.fillTop;
  const fillBottom = disabled ? palette.fillShadow : palette.fillBottom;

  if (shape === "tile") {
    return (
      <svg
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
      >
        <defs>
          <linearGradient id={`${idPrefix}-outer`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={palette.outerDark} />
            <stop offset="100%" stopColor="#09030f" />
          </linearGradient>
          <linearGradient id={`${idPrefix}-gold`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={palette.goldBright} />
            <stop offset="52%" stopColor={palette.goldMid} />
            <stop offset="100%" stopColor={palette.goldDark} />
          </linearGradient>
          <linearGradient id={`${idPrefix}-fill`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={fillTop} />
            <stop offset="58%" stopColor={fillBase} />
            <stop offset="100%" stopColor={fillBottom} />
          </linearGradient>
        </defs>
        <path
          d="M20 4 H80 L96 20 V80 L80 96 H20 L4 80 V20 Z"
          fill={`url(#${idPrefix}-outer)`}
        />
        <path
          d="M22 9 H78 L91 22 V78 L78 91 H22 L9 78 V22 Z"
          fill="none"
          stroke={`url(#${idPrefix}-gold)`}
          strokeWidth="3"
        />
        <path
          d="M28 15 H72 L84 27 V73 L72 85 H28 L16 73 V27 Z"
          fill={`url(#${idPrefix}-fill)`}
          stroke={palette.accent}
          strokeOpacity="0.75"
          strokeWidth="1.4"
        />
        <path d="M28 15 H72 L84 27 V35 H16 V27 Z" fill={palette.topHighlight} />
        <path d="M28 85 H72 L84 73 V66 H16 V73 Z" fill={palette.edgeShadow} opacity="0.8" />
      </svg>
    );
  }

  if (shape === "menu") {
    return (
      <svg
        viewBox="0 0 320 96"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
      >
        <defs>
          <linearGradient id={`${idPrefix}-outer`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#190a2a" />
            <stop offset="100%" stopColor="#09030f" />
          </linearGradient>
          <linearGradient id={`${idPrefix}-gold`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={palette.goldBright} />
            <stop offset="52%" stopColor={palette.goldMid} />
            <stop offset="100%" stopColor={palette.goldDark} />
          </linearGradient>
          <linearGradient id={`${idPrefix}-fill`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={fillTop} />
            <stop offset="58%" stopColor={fillBase} />
            <stop offset="100%" stopColor={fillBottom} />
          </linearGradient>
        </defs>
        <path
          d="M24 6 H294 L314 30 V66 L294 90 H24 L6 48 Z"
          fill={`url(#${idPrefix}-outer)`}
        />
        <path
          d="M30 10 H291 L307 30 V66 L291 86 H30 L13 48 Z"
          fill="none"
          stroke={`url(#${idPrefix}-gold)`}
          strokeWidth="3"
        />
        <path
          d="M38 16 H282 L296 32 V64 L282 80 H38 L23 48 Z"
          fill={`url(#${idPrefix}-fill)`}
          stroke={palette.accent}
          strokeOpacity="0.74"
          strokeWidth="1.4"
        />
        <path d="M38 16 H282 L296 32 V40 H23 L32 27 Z" fill={palette.topHighlight} opacity="0.92" />
        <path d="M38 80 H282 L296 64 V56 H23 L32 69 Z" fill={palette.edgeShadow} opacity="0.88" />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 240 64"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      width="100%"
      height="100%"
    >
      <defs>
        <linearGradient id={`${idPrefix}-outer`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#190a2a" />
          <stop offset="100%" stopColor="#09030f" />
        </linearGradient>
        <linearGradient id={`${idPrefix}-gold`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={palette.goldBright} />
          <stop offset="52%" stopColor={palette.goldMid} />
          <stop offset="100%" stopColor={palette.goldDark} />
        </linearGradient>
        <linearGradient id={`${idPrefix}-fill`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={fillTop} />
          <stop offset="58%" stopColor={fillBase} />
          <stop offset="100%" stopColor={fillBottom} />
        </linearGradient>
      </defs>
      <path
        d="M20 4 H216 L236 20 V44 L216 60 H20 L4 32 Z"
        fill={`url(#${idPrefix}-outer)`}
      />
      <path
        d="M24 8 H213 L229 22 V42 L213 56 H24 L10 32 Z"
        fill="none"
        stroke={`url(#${idPrefix}-gold)`}
        strokeWidth="2.8"
      />
      <path
        d="M30 13 H206 L220 24 V40 L206 51 H30 L18 32 Z"
        fill={`url(#${idPrefix}-fill)`}
        stroke={palette.accent}
        strokeOpacity="0.72"
        strokeWidth="1.2"
      />
      <path d="M30 13 H206 L220 24 V29 H18 L25 22 Z" fill={palette.topHighlight} opacity="0.9" />
      <path d="M30 51 H206 L220 40 V35 H18 L25 42 Z" fill={palette.edgeShadow} opacity="0.88" />
    </svg>
  );
}

function IconMedallion({ idPrefix, color, disabled }) {
  const palette = getIconMedallionPalette(color);
  const fillBase = disabled ? palette.disabledBase : palette.base;
  const fillTop = disabled ? palette.disabledBase : palette.medallionTop;
  const fillBottom = disabled ? palette.fillShadow : palette.medallionBottom;

  return (
    <svg
      viewBox="0 0 56 56"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      width="100%"
      height="100%"
    >
      <defs>
        <linearGradient id={`${idPrefix}-outer`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#2f1248" />
          <stop offset="100%" stopColor="#13071f" />
        </linearGradient>
        <linearGradient id={`${idPrefix}-gold`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={palette.goldBright} />
          <stop offset="52%" stopColor={palette.goldMid} />
          <stop offset="100%" stopColor={palette.goldDark} />
        </linearGradient>
        <linearGradient id={`${idPrefix}-fill`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={fillTop} />
          <stop offset="55%" stopColor={fillBase} />
          <stop offset="100%" stopColor={fillBottom} />
        </linearGradient>
      </defs>
      <polygon points="28,3 47,14 53,28 47,42 28,53 9,42 3,28 9,14" fill={`url(#${idPrefix}-outer)`} />
      <polygon
        points="28,6 44,15 49,28 44,41 28,50 12,41 7,28 12,15"
        fill="none"
        stroke={`url(#${idPrefix}-gold)`}
        strokeWidth="2.4"
      />
      <polygon
        points="28,10 41,17 45,28 41,39 28,46 15,39 11,28 15,17"
        fill={`url(#${idPrefix}-fill)`}
        stroke={palette.accent}
        strokeOpacity="0.8"
        strokeWidth="1"
      />
      <polygon points="28,10 41,17 45,28 11,28 15,17" fill={palette.topHighlight} opacity="0.92" />
    </svg>
  );
}

function RenderNeoIcon({ icon, label, iconStyle = {}, contentSx = {} }) {
  if (React.isValidElement(icon)) {
    return (
      <Box
        sx={{
          width: "68%",
          height: "68%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff7cb",
          ...contentSx,
        }}
      >
        {icon}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={icon}
      alt={label || ""}
      sx={{
        width: "68%",
        height: "68%",
        objectFit: "contain",
        pointerEvents: "none",
        ...iconStyle,
      }}
    />
  );
}

function CompactPill({
  icon,
  label,
  endAdorn,
  disabled,
  redDot,
  isLocked,
  innerStyle,
  labelStyle,
  iconStyle,
  additionalStyle,
  onClick,
  dataDriver,
}) {
  const pillId = React.useId().replace(/:/g, "");
  const palette = getChromePalette("#4b2cff");
  const textShadow = getContentTextShadow(disabled);

  return (
    <Box
      data-driver={dataDriver}
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        all: "unset",
        position: "relative",
        display: "inline-flex",
        width: "fit-content",
        minWidth: { xs: "78px", md: "96px" },
        height: { xs: "32px", md: "36px" },
        cursor: disabled ? "default" : "pointer",
        filter: getWrapperFilter(disabled),
        boxSizing: "border-box",
        ...additionalStyle,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          filter: "drop-shadow(0 7px 12px rgba(0, 0, 0, 0.3))",
        }}
      >
        <FrameSvg idPrefix={`neo-item-compact-${pillId}`} palette={palette} shape="compact" disabled={disabled} />
      </Box>

      <Box
        sx={{
          position: "absolute",
          left: { xs: 4, md: 5 },
          top: "50%",
          transform: "translateY(-50%)",
          width: { xs: 26, md: 30 },
          height: { xs: 26, md: 30 },
          pointerEvents: "none",
        }}
      >
        <IconMedallion idPrefix={`neo-item-medallion-${pillId}`} color="#7f40ff" disabled={disabled} />
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          pl: { xs: "32px", md: "36px" },
          pr: 1.5,
          boxSizing: "border-box",
          gap: 0.75,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: { xs: 4, md: 5 },
            top: "50%",
            transform: "translateY(-50%)",
            width: { xs: 26, md: 30 },
            height: { xs: 26, md: 30 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <RenderNeoIcon icon={icon} label={label} iconStyle={iconStyle} />
        </Box>

        <Typography
          component="span"
          sx={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            minWidth: 0,
            color: "#ffe99f",
            fontWeight: 900,
            letterSpacing: "0.03em",
            textTransform: "uppercase",
            fontSize: { xs: "0.7rem", md: "0.82rem" },
            textAlign: "center",
            textShadow,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...labelStyle,
          }}
        >
          {label}
        </Typography>

        {endAdorn ? (
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffe99f",
            }}
          >
            {endAdorn}
          </Box>
        ) : null}
      </Box>

      {redDot && (
        <Box
          sx={{
            position: "absolute",
            top: -4,
            right: -2,
            width: 14,
            height: 14,
            borderRadius: "50%",
            backgroundColor: "#ef4444",
            border: "2px solid #190a2a",
            zIndex: 3,
          }}
        />
      )}

      {isLocked && (
        <Box
          component="img"
          src={require("../@shared/assets/icons/lock_deck_icon2.png")}
          alt="locked"
          sx={{
            width: 20,
            height: 20,
            position: "absolute",
            right: -6,
            top: -5,
            zIndex: 3,
          }}
        />
      )}
    </Box>
  );
}

function MenuPill({
  icon,
  label,
  disabled,
  redDot,
  isLocked,
  iconStyle,
  labelStyle,
  additionalStyle,
  onClick,
  dataDriver,
  isLevelBadge,
  levelValue,
}) {
  const menuId = React.useId().replace(/:/g, "");
  const palette = getChromePalette("#6f2dff");
  const textShadow = getContentTextShadow(disabled);

  return (
    <Box
      data-driver={dataDriver}
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        all: "unset",
        position: "relative",
        display: "block",
        width: "100%",
        minWidth: { xs: "124px", md: "196px" },
        height: { xs: "44px", md: "58px" },
        cursor: disabled ? "default" : "pointer",
        filter: getWrapperFilter(disabled),
        boxSizing: "border-box",
        ...additionalStyle,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          filter: "drop-shadow(0 10px 16px rgba(0, 0, 0, 0.34))",
        }}
      >
        <FrameSvg idPrefix={`neo-item-menu-${menuId}`} palette={palette} shape="menu" disabled={disabled} />
      </Box>

      <Box
        sx={{
          position: "absolute",
          left: { xs: 10, md: 14 },
          top: "50%",
          transform: "translateY(-50%)",
          width: { xs: 28, md: 36 },
          height: { xs: 28, md: 36 },
          pointerEvents: "none",
        }}
      >
        <IconMedallion idPrefix={`neo-item-menu-medallion-${menuId}`} color="#9340ff" disabled={disabled} />
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          pl: { xs: "44px", md: "58px" },
          pr: { xs: 2, md: 2.25 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: { xs: 10, md: 14 },
            top: "50%",
            transform: "translateY(-50%)",
            width: { xs: 28, md: 36 },
            height: { xs: 28, md: 36 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <RenderNeoIcon icon={icon} label={label} iconStyle={iconStyle} />
        </Box>

        <Typography
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            color: "#ffe99f",
            textAlign: "left",
            fontWeight: 900,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            fontSize: { xs: "0.76rem", md: "0.98rem" },
            textShadow,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...labelStyle,
          }}
        >
          {label}
        </Typography>

        {isLevelBadge ? (
          <Typography
            component="span"
            sx={{
              color: "#fff6cf",
              fontWeight: 900,
              fontSize: { xs: "0.65rem", md: "0.8rem" },
              textShadow,
              ml: 1,
            }}
          >
            LVL {levelValue}
          </Typography>
        ) : null}
      </Box>

      {redDot && (
        <Box
          sx={{
            position: "absolute",
            top: -4,
            right: 6,
            width: 16,
            height: 16,
            borderRadius: "50%",
            backgroundColor: "#ef4444",
            border: "2px solid #190a2a",
            zIndex: 3,
          }}
        />
      )}

      {isLocked && (
        <Box
          component="img"
          src={require("../@shared/assets/icons/lock_deck_icon2.png")}
          alt="locked"
          sx={{
            width: { xs: 20, md: 24 },
            height: { xs: 20, md: 24 },
            position: "absolute",
            right: { xs: -6, md: -8 },
            top: { xs: -4, md: -5 },
            zIndex: 3,
          }}
        />
      )}
    </Box>
  );
}

function TileItem({
  icon,
  label,
  disabled,
  redDot,
  isLocked,
  iconStyle = {},
  labelStyle = {},
  additionalStyle = {},
  onClick,
  tileSize = { xs: 70, md: 90 },
  tileBg = "#5b57ff",
  innerStyle = {},
  iconScale = 0.55,
  labelPosition = "below",
  dataDriver,
}) {
  const tileId = React.useId().replace(/:/g, "");
  const palette = getChromePalette(tileBg || "#5b57ff");

  return (
    <Box
      role="listitem"
      data-driver={dataDriver}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        userSelect: "none",
        cursor: disabled ? "default" : "pointer",
        pointerEvents: disabled ? "none" : "auto",
        filter: getWrapperFilter(disabled),
        "& .neo-tile-label": {
          opacity: { xs: 1, md: 0 },
          display: { xs: "flex", md: "none" },
          transition: "opacity .18s ease",
        },
        "&:hover .neo-tile-label": {
          opacity: { md: 1 },
          display: { md: "flex" },
        },
        ...additionalStyle,
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          width: tileSize,
          height: tileSize,
          position: "relative",
          overflow: "visible",
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            filter: "drop-shadow(0 10px 16px rgba(0, 0, 0, 0.34))",
          }}
        >
          <FrameSvg idPrefix={`neo-item-tile-${tileId}`} palette={palette} shape="tile" disabled={disabled} />
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...innerStyle,
          }}
        >
          {React.isValidElement(icon) ? (
            <Box
              sx={{
                width: `calc(${iconScale * 100}% - 4px)`,
                height: `calc(${iconScale * 100}% - 4px)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
          ) : (
            <Box
              component="img"
              src={icon}
              alt={label}
              sx={{
                width: `${iconScale * 100}%`,
                height: `${iconScale * 100}%`,
                objectFit: "contain",
                pointerEvents: "none",
                ...iconStyle,
              }}
            />
          )}
        </Box>

        {redDot && (
          <Box
            sx={{
              position: "absolute",
              top: 2,
              right: 2,
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: "#ef4444",
              border: "2px solid #190a2a",
              zIndex: 3,
            }}
          />
        )}

        {isLocked && (
          <Box
            component="img"
            src={require("../@shared/assets/icons/lock_deck_icon2.png")}
            alt="locked"
            sx={{
              width: 24,
              height: 24,
              position: "absolute",
              right: -4,
              top: -4,
              zIndex: 3,
            }}
          />
        )}
      </Box>

      {label && labelPosition === "below" && (
        <Typography
          className="neo-tile-label"
          sx={{
            mt: { xs: 0, sm: 0, md: 1 },
            fontWeight: 900,
            fontSize: { xs: 12, md: 14 },
            textAlign: "center",
            color: labelStyle?.color || "#fff",
            textShadow: labelStyle?.textShadow || getContentTextShadow(disabled),
            textTransform: "uppercase",
            ...labelStyle,
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
}

export default function NeoMenuItem({
  icon,
  iconTop = false,
  isLevelBadge = false,
  isRounded = true,
  additionalStyle = {},
  label,
  onClick,
  disabled,
  endAdorn,
  redDot,
  isLocked,
  innerStyle = {},
  labelStyle = {},
  iconStyle = {},
  variant = "legacy",
  labelPosition = "below",
  tileSize = { xs: 70, md: 90 },
  tileRadius = { xs: 4, md: 6 },
  tileBg = "#5a66ff",
  tileOutline = "3px solid #000",
  tileShadow = "0 8px 20px rgba(0,0,0,.35)",
  iconScale = 0.55,
  dataDriver,
}) {
  const { userData } = useAuthStore();
  const { Theme } = React.useContext(MainContext);

  void iconTop;
  void isRounded;
  void tileRadius;
  void tileBg;
  void tileOutline;
  void tileShadow;
  void Theme;

  if (variant === "tile") {
    return (
      <TileItem
        icon={icon}
        label={label}
        disabled={disabled}
        redDot={redDot}
        isLocked={isLocked}
        iconStyle={iconStyle}
        labelStyle={labelStyle}
        additionalStyle={additionalStyle}
        onClick={onClick}
        tileSize={tileSize}
        tileBg={tileBg}
        innerStyle={innerStyle}
        iconScale={iconScale}
        labelPosition={labelPosition}
        dataDriver={dataDriver}
      />
    );
  }

  if (variant === "menu") {
    return (
      <MenuPill
        icon={icon}
        label={label}
        disabled={disabled}
        redDot={redDot}
        isLocked={isLocked}
        iconStyle={iconStyle}
        labelStyle={labelStyle}
        additionalStyle={additionalStyle}
        onClick={onClick}
        dataDriver={dataDriver}
        isLevelBadge={isLevelBadge}
        levelValue={userData?.level || 1}
      />
    );
  }

  return (
    <CompactPill
      icon={icon}
      label={label}
      endAdorn={endAdorn}
      disabled={disabled}
      redDot={redDot}
      isLocked={isLocked}
      innerStyle={innerStyle}
      labelStyle={labelStyle}
      iconStyle={iconStyle}
      additionalStyle={additionalStyle}
      onClick={onClick}
      dataDriver={dataDriver}
    />
  );
}

export function LegacyLevelProgress({ Theme, userData }) {
  const currentLevel = parseInt(userData.level || "1", 10);
  const currentXP = userData.xp || 0;

  const currentLevelData = levelXPTable.find(
    (lvl) => parseInt(lvl.level, 10) === currentLevel
  );
  const nextLevelData = levelXPTable.find(
    (lvl) => parseInt(lvl.level, 10) === currentLevel + 1
  );

  const xpIntoLevel = currentXP - (currentLevelData?.cumulativeXP || 0);
  const xpNeeded = nextLevelData
    ? nextLevelData.cumulativeXP - currentLevelData.cumulativeXP
    : 1;

  const progressPercent = Math.min(
    100,
    Math.max(0, Math.round((xpIntoLevel / xpNeeded) * 100))
  );

  return (
    <>
      <div
        style={{
          width: "100%",
          height: "6px",
          borderRadius: "10px",
          border: "2px solid #000",
          backgroundColor: Theme.palette.text.main,
          zIndex: 30,
          marginTop: 10,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${progressPercent}%`,
            backgroundColor: Theme.palette.border.dark,
            transition: "width 0.3s ease-in-out",
          }}
        />
      </div>
      <Typography
        sx={{
          width: "100%",
          textAlign: "center",
          fontWeight: 900,
          fontSize: 8,
          color: "#fff",
          textShadow:
            "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
          zIndex: 31,
          marginTop: "-5px",
        }}
      >
        {progressPercent}%
      </Typography>
    </>
  );
}
